import { ValueFormatterParams } from "ag-grid-community";
import React, { useRef } from "react";
import Grid from "../../../../components/Grid";
import { ColumnDef, GridHandle } from "../../../../components/Grid/model/Grid";
import { usePermissions } from "../../../../hooks/PermissionContext";
import { IFinanceiroMovimentacao, IFinanceiroMovimentacaoPagamento } from "./types/types";
import Info from "./Info";
import ExportModal from "./components/ExportModal/index";
import ExportIcon from "../../../../assets/images/exportIcon.svg";
import Can from "../../../../components/Can";


const FinanceiroMovimentacoes: React.FC = () => {
  const hasEstornosPermission = usePermissions("ESTORNADOS");
  const [selectedRow, setSelectedRow] = React.useState<IFinanceiroMovimentacao | undefined>();
  const [isView, setIsView] = React.useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = React.useState(false);
  const [activeView, setActiveView] = React.useState<"concluidos" | "estornados">("concluidos");
  const [customFilters, setCustomFilters] = React.useState<Record<string, unknown>>({
    tipo_visualizacao: "concluidos",
  });
  const [gridKeyCounter, setGridKeyCounter] = React.useState(0);
  const gridRef = useRef<GridHandle>(null);

  const formatCurrency = (value: number | string) => {
    const numericValue = Number(value);
    if (Number.isNaN(numericValue)) return "-";

    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(numericValue);
  };

  const getPagamento = (
    data: IFinanceiroMovimentacao | undefined,
    index: number
  ): IFinanceiroMovimentacaoPagamento | undefined => {
    return data?.pagamentos?.[index];
  };

  const getValorTotalTriagem = (data: IFinanceiroMovimentacao | undefined) => {
    return (
      data?.valor_total_triagem ??
      data?.valor_total_da_triagem ??
      data?.custo_triagem ??
      data?.pagamentos?.reduce((total, pagamento) => total + Number(pagamento.valor_total || 0), 0)
    );
  };

  const paymentTypeColumns: ColumnDef[] = [1, 2, 3, 4].map((paymentNumber) => ({
    field: `tipo_pagamento_${paymentNumber}`,
    headerName: `Tipo Pagamento ${paymentNumber}`,
    filter: false,
    sortable: false,
    width: 190,
    valueGetter: (params) => getPagamento(params.data, paymentNumber - 1)?.tipo_pagamento ?? "---",
  }));

  const paymentValueColumns: ColumnDef[] = [1, 2, 3, 4].map((paymentNumber) => ({
    field: `valor_pagamento_${paymentNumber}`,
    headerName: `Valor Pagamento ${paymentNumber}`,
    filter: false,
    sortable: false,
    width: 180,
    type: "numberColumn",
    valueGetter: (params) => getPagamento(params.data, paymentNumber - 1)?.valor_total,
    valueFormatter: (params: ValueFormatterParams) => {
      if (params.value !== undefined && params.value !== null) {
        return formatCurrency(params.value);
      }
      return "-";
    },
  }));

  const estornoColumns: ColumnDef[] = activeView === "estornados"
    ? [
        {
          field: "justificativa_estorno",
          fieldName: "justificativa_estorno",
          headerName: "Justificativa Estorno",
          filter: true,
          sortable: false,
          width: 320,
          valueGetter: (params) => {
            return String(params.data?.justificativa_estorno || "").trim() || "---";
          },
        },
        {
          field: "situacao_estorno",
          fieldName: "situacao_estorno",
          headerName: "Situação do Estorno",
          filter: true,
          filterParams: {
            selected: {
              isMultiple: false,
              data: [
                { value: "Concluida", label: "Concluído" },
                { value: "Não concluida", label: "Não Concluído" },
              ],
            },
          },
          sortable: false,
          width: 220,
          valueGetter: (params) => {
            const estornoSemFinalizacao =
              params.data?.estorno_sem_finalizacao === true ||
              params.data?.estorno_sem_finalizacao === "t" ||
              params.data?.estorno_sem_finalizacao === 1 ||
              params.data?.estorno_sem_finalizacao === "1";

            return estornoSemFinalizacao
              ? "Não concluida"
              : "Concluida";
          },
        },
      ]
    : [];

  const columns: ColumnDef[] = [
    {
      field: "id_operacao_patio",
      fieldName: "id_operacao_patio",
      headerName: "ID Op. Pátio",
      filter: true,
      width: 140,
      type: "numberColumn",
    },
    {
      field: "id_bumerangue",
      fieldName: "id_bumerangue",
      headerName: "ID Bumerangue",
      filter: true,
      width: 150,
    },
    {
      field: "data_hora_pagamento",
      fieldName: "data_hora_pagamento",
      headerName: "Data Pagamento",
      filter: true,
      filterParams: { dateBetween: true },
      width: 200,
      type: "dateColumn",
    },
    {
      field: "data_hora",
      fieldName: "data_hora",
      headerName: "Data Entrada",
      filter: true,
      filterParams: { dateBetween: true },
      width: 200,
      type: "dateColumn",
    },
    {
      field: "data_hora_saida",
      fieldName: "data_hora_saida",
      headerName: "Data Saída",
      filter: true,
      filterParams: { dateBetween: true },
      width: 200,
      type: "dateColumn",
    },
    {
      field: "tempo_patio",
      fieldName: "tempo_patio",
      headerName: "Tempo Pátio",
      filter: true,
      width: 150,
    },
    {
      field: "tipo_agendamento",
      fieldName: "tipo_agendamento",
      headerName: "Tipo Agendamento",
      filter: true,
      filterParams: {
        selected: {
          isMultiple: false,
          data: [
            { value: "Triagem", label: "Triagem" },
            { value: "Carrossel", label: "Carrossel" },
            { value: "Estadia", label: "Estadia" },
          ],
        },
      },
      width: 180,
    },
    {
      field: "data_agendamento",
      fieldName: "data_agendamento",
      headerName: "Data Agendamento",
      filter: true,
      filterParams: { dateBetween: true },
      width: 200,
      type: "dateColumn",
    },
    {
      field: "tipo_operacao",
      fieldName: "tipo_operacao",
      headerName: "Tipo Operação",
      filter: true,
      filterParams: {
        selected: {
          isMultiple: false,
          data: [
            { value: "Carga", label: "Carga" },
            { value: "Descarga", label: "Descarga" },
          ],
        },
      },
      width: 150,
    },
    {
      field: "terminal_nome",
      fieldName: "terminal_nome",
      headerName: "Terminal",
      filter: true,
      width: 250,
    },
    {
      field: "terminal_cnpj",
      fieldName: "terminal_cnpj",
      headerName: "CNPJ Terminal",
      filter: true,
      width: 180,
    },
    {
      field: "transportadora_nome",
      fieldName: "transportadora_nome",
      headerName: "Transportadora",
      filter: true,
      width: 250,
    },
    {
      field: "transportadora_cnpj",
      fieldName: "transportadora_cnpj",
      headerName: "CNPJ Transportadora",
      filter: true,
      width: 180,
    },
    {
      field: "motorista_nome",
      fieldName: "motorista_nome",
      headerName: "Motorista",
      filter: true,
      width: 200,
    },
    {
      field: "motorista_cpf",
      fieldName: "motorista_cpf",
      headerName: "CPF Motorista",
      filter: true,
      width: 150,
    },
    {
      field: "motorista_telefone",
      fieldName: "motorista_telefone",
      headerName: "Telefone Motorista",
      filter: true,
      width: 150,
    },
    {
      field: "placa",
      fieldName: "placa",
      headerName: "Placa Dianteira",
      filter: true,
      width: 150,
    },
    {
      field: "placa_traseira",
      fieldName: "placa_traseira",
      headerName: "Placa Traseira",
      filter: true,
      width: 150,
    },
    {
      field: "tipo_veiculo",
      fieldName: "tipo_veiculo",
      headerName: "Veículo",
      filter: true,
      width: 150,
    },
    {
      field: "tipo_carga",
      fieldName: "tipo_carga",
      headerName: "Tipo de Carga",
      filter: true,
      width: 150,
    },
    {
      field: "identificacao_carga",
      fieldName: "identificacao_carga",
      headerName: "Carga (Vazio/Cheio)",
      filter: true,
      width: 180,
    },
    {
      field: "container_agendamento",
      fieldName: "container_agendamento",
      headerName: "Container (Agendamento)",
      filter: true,
      width: 220,
    },
    {
      field: "container_entrada",
      fieldName: "container_entrada",
      headerName: "Container (Entrada)",
      filter: true,
      width: 220,
    },
    ...estornoColumns,
    {
      field: "valor_total_triagem",
      headerName: "Valor Total Triagem",
      filter: false,
      sortable: false,
      width: 150,
      type: "numberColumn",
      valueGetter: (params) => getValorTotalTriagem(params.data),
      valueFormatter: (params: ValueFormatterParams) => {
        if (params.value !== undefined && params.value !== null) {
          return formatCurrency(params.value);
        }
        return "-";
      },
    },
    ...paymentTypeColumns,
    ...paymentValueColumns,
    {
      field: "custo_estadia",
      fieldName: "custo_estadia",
      headerName: "Custo Estadia",
      filter: true,
      width: 150,
      type: "numberColumn",
      valueFormatter: (params: ValueFormatterParams) => {
        if (params.value !== undefined && params.value !== null) {
          return formatCurrency(Number(params.value));
        }
        return "-";
      },
    },
    {
      field: "custo_meia_estadia",
      fieldName: "custo_meia_estadia",
      headerName: "Custo Meia Estadia",
      filter: true,
      width: 180,
      type: "numberColumn",
      valueFormatter: (params: ValueFormatterParams) => {
        if (params.value !== undefined && params.value !== null) {
          return formatCurrency(Number(params.value));
        }
        return "-";
      },
    },
    {
      field: "custo_hora_extra",
      fieldName: "custo_hora_extra",
      headerName: "Custo Hora Extra",
      filter: true,
      width: 170,
      type: "numberColumn",
      valueFormatter: (params: ValueFormatterParams) => {
        if (params.value !== undefined && params.value !== null) {
          return formatCurrency(Number(params.value));
        }
        return "-";
      },
    },
    {
      field: "custo_triagem",
      fieldName: "custo_triagem",
      headerName: "Custo Triagem",
      filter: true,
      width: 150,
      type: "numberColumn",
      valueFormatter: (params: ValueFormatterParams) => {
        if (params.value !== undefined && params.value !== null) {
          return formatCurrency(Number(params.value));
        }
        return "-";
      },
    },
    {
      field: "usuario_pagamento",
      fieldName: "usuario_pagamento",
      headerName: "Usuário",
      filter: true,
      width: 200,
    },
    {
      field: "supervisor",
      fieldName: "supervisor",
      headerName: "Supervisor",
      filter: true,
      width: 180,
    },
    {
      field: "numero_transacao",
      fieldName: "numero_transacao",
      headerName: "Transação",
      filter: true,
      width: 150,
    },
    {
      field: "qtd_horas_extras",
      fieldName: "qtd_horas_extras",
      headerName: "Horas Extras",
      filter: true,
      width: 140,
      type: "numberColumn",
    },
  ];

  return (
    <>
      {isView && (
        <Info
          data={selectedRow}
          title="Conhecer - Movimentação Financeira"
          onClose={() => setIsView(false)}
        />
      )}

      <ExportModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        currentFilters={gridRef.current?.getFilters() || {}}
        currentView={activeView}
      />

      <div className="flex flex-col w-full min-w-0 h-screen overflow-x-hidden bg-[#F5F5F5] p-5">
        <div className="flex items-center justify-between w-full mb-3">
          <div>
            <h1 className="text-2xl text-[#000000] font-bold">Financeiro - Movimentações</h1>
          </div>
          <div className="flex items-center shrink-0 gap-3">
            {hasEstornosPermission && (
              <>
                <button
                  className={
                    `flex items-center justify-center h-10 px-4 text-sm font-light rounded-full ` +
                    (activeView === "concluidos"
                      ? "bg-[#062D4E] text-white border-none"
                      : "bg-white border border-[#CCCCCC] text-[#062D4E]")
                  }
                  onClick={() => {
                    if (activeView === "concluidos") {
                      return;
                    }

                    setActiveView("concluidos");
                    setCustomFilters({ tipo_visualizacao: "concluidos" });
                    setGridKeyCounter((current) => current + 1);
                  }}
                >
                  Concluídos
                </button>

                <button
                  className={
                    `flex items-center justify-center h-10 px-4 text-sm font-light rounded-full ` +
                    (activeView === "estornados"
                      ? "bg-[#062D4E] text-white border-none"
                      : "bg-white border border-[#062D4E] text-[#062D4E]")
                  }
                  onClick={() => {
                    if (activeView === "estornados") {
                      return;
                    }

                    setActiveView("estornados");
                    setCustomFilters({ tipo_visualizacao: "estornados" });
                    setGridKeyCounter((current) => current + 1);
                  }}
                >
                  Estornados
                </button>
              </>
            )}

            <Can acao="EXPORTAR">
            <button
              className="flex items-center justify-center h-12 w-36 bg-[#062D4E] text-[#FFFFFF] text-sm font-light border-none rounded-full gap-2 hover:bg-[#0a3d6a] transition-colors"
              onClick={() => setIsExportModalOpen(true)}
            >
              <span>Exportar</span>
              <img src={ExportIcon} alt="" className="w-4 h-4 invert" />
            </button>
            </Can>
          </div>
        </div>

        <div className="flex w-full min-w-0 h-full overflow-hidden">
          <Grid
            key={`${activeView}-${gridKeyCounter}`}
            ref={gridRef}
            columns={columns}
            filters={[]}
            customFilters={customFilters}
            pagination
            path="/listar/financeiroMovimentacoes"
            onView={(data: IFinanceiroMovimentacao) => {
              setSelectedRow(data);
              setIsView(true);
            }}
            status={[]}
            persistFiltersKey={`financeiro_movimentacoes_${activeView}_filters_v2`}
            defaultSortField="data_hora_pagamento"
            perPage={20}
          />
        </div>
      </div>
    </>
  );
};

export default FinanceiroMovimentacoes;
